import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useI18n } from "../../i18n";

const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();

// Searchable customer picker for thousands of customers: type code / name / ΑΦΜ / area, pick with mouse or Enter.
export const CustomerPicker = ({ customers, value, onChange, required = false, placeholder, className = "", testId = "customer-picker" }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const box = useRef(null);
  const selected = customers.find((c) => c.code === value);
  const list = useMemo(() => {
    const nq = norm(q.trim());
    if (!nq) return customers.slice(0, 50);
    const terms = nq.split(/\s+/);
    return customers.filter((c) => { const h = norm(`${c.code} ${c.name} ${c.vat_number || ""} ${c.area || ""} ${c.island || ""}`); return terms.every((x) => h.includes(x)); }).slice(0, 50);
  }, [customers, q]);
  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const pick = (c) => { onChange(c.code); setOpen(false); setQ(""); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (list[hi]) pick(list[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div ref={box} className={`relative ${className}`} data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left text-sm" data-testid={`${testId}-toggle`} aria-haspopup="listbox" aria-expanded={open}>
        <span className={`truncate ${selected ? "text-navy" : "text-slate-400"}`}>{selected ? `${selected.code} · ${selected.name}` : value || placeholder || t("users.pickCustomer")}</span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {required && <input tabIndex={-1} aria-hidden className="pointer-events-none absolute inset-0 h-0 w-0 opacity-0" required value={value || ""} onChange={() => {}} />}
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[18rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" role="listbox">
          <label className="flex items-center gap-2 border-b border-slate-100 px-3">
            <Search className="h-4 w-4 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder={t("users.searchCustomer")} className="h-10 w-full bg-transparent text-sm outline-none" data-testid={`${testId}-search`} />
          </label>
          <ul className="max-h-72 overflow-auto py-1 text-sm">
            {list.map((c, i) => (
              <li key={c.code} role="option" aria-selected={c.code === value} onMouseEnter={() => setHi(i)} onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-2 ${i === hi ? "bg-canvas" : ""}`} data-testid={`${testId}-option-${c.code}`}>
                <span className="min-w-0"><span className="font-semibold text-navy">{c.code}</span> <span className="text-slate-700">{c.name}</span>{(c.area || c.island) && <span className="block truncate text-xs text-slate-500">{[c.area, c.island].filter(Boolean).join(" · ")}{c.vat_number ? ` · ΑΦΜ ${c.vat_number}` : ""}</span>}</span>
                {c.code === value && <Check className="h-4 w-4 shrink-0 text-navy" />}
              </li>
            ))}
            {!list.length && <li className="px-3 py-3 text-slate-500" data-testid={`${testId}-empty`}>{t("users.noCustomerMatch")}</li>}
            {list.length === 50 && <li className="px-3 py-1.5 text-xs text-slate-400">{t("users.refineSearch")}</li>}
          </ul>
        </div>
      )}
    </div>
  );
};
